import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Coins, ScrollText, Compass, Loader2 } from "lucide-react";
import { PageHeader, useTitle } from "@/components/PageHeader";
import { Reveal, Overline } from "@/components/Reveal";
import { api, asset } from "@/lib/api";
import { JOBS, IMAGES } from "@/data/content";
import { useSiteConfig } from "@/context/SiteConfig";

const JOB_IMG = [IMAGES.town, IMAGES.ridge, IMAGES.townRide];
const TAG_IMG = {
  Law: IMAGES.town, Trade: IMAGES.townCattle, Medical: IMAGES.manifesto,
  Craft: IMAGES.ridge, Service: IMAGES.townRide, Retail: IMAGES.goldRider,
};

export default function JobDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { link } = useSiteConfig();
  const [jobs, setJobs] = useState(JOBS);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/jobs").then(({ data }) => { if (data?.length) setJobs(data); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const idx = jobs.findIndex((j) => j.slug === slug);
  const job = jobs[idx];
  useTitle(job ? `${job.title} | Aurora Springs RP Jobs` : "Jobs & Careers | Aurora Springs RP | RedM Roleplay", job && job.intro ? job.intro : "Explore career paths in Aurora Springs RP, an 18+ RedM Wild West roleplay server set in 1895.");

  if (loading && !job) return <div className="pt-40 pb-32 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-teal" /></div>;
  if (!job) {
    return (
      <div className="pt-40 pb-32 text-center">
        <p className="font-display text-4xl">Trade not found.</p>
        <Link to="/jobs" className="mt-6 inline-block font-feature text-xs uppercase tracking-widest text-teal">← Back to Jobs</Link>
      </div>
    );
  }
  const image = job.image ? asset(job.image) : (TAG_IMG[job.tag] || JOB_IMG[(idx < 0 ? 0 : idx) % JOB_IMG.length]);
  const others = jobs.filter((j) => j.slug !== slug).slice(0, 3);

  return (
    <>
      <PageHeader overline={`Jobs / ${job.tag}`} title={job.title} image={image} sub={job.intro} />
      <section className="mx-auto max-w-6xl px-6 sm:px-10 py-16">
        <button onClick={() => navigate("/jobs")} data-testid="job-back-btn"
          className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest text-[#8F877C] hover:text-teal transition-colors mb-12">
          <ArrowLeft className="h-4 w-4" /> All Trades
        </button>
        <div className="grid gap-12 lg:grid-cols-[1fr_320px] items-start">
          <div className="space-y-12">
            {job.duties?.length > 0 && (
              <Reveal>
                <Overline>Duties</Overline>
                <ul className="mt-6 space-y-4">
                  {job.duties.map((d, i) => (
                    <li key={i} className="flex gap-4 text-[#b8b0a4] leading-relaxed" data-testid={`job-duty-${i}`}>
                      <Check className="h-5 w-5 text-teal shrink-0 mt-0.5" /><span>{d}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            )}
            {job.howto && (
              <Reveal delay={0.1}>
                <Overline>How to start</Overline>
                <p className="mt-6 text-[#b8b0a4] leading-relaxed max-w-2xl">{job.howto}</p>
                <a href={link("connect")} target="_blank" rel="noreferrer" data-testid="job-connect-btn"
                  className="mt-8 inline-flex items-center gap-2 font-feature text-sm uppercase tracking-widest bg-teal text-obsidian px-8 py-4 hover:bg-bone transition-colors">Connect &amp; Begin</a>
              </Reveal>
            )}
          </div>
          <Reveal delay={0.15}>
            <div className="border border-[#2A201A] bg-surface/40 divide-y divide-[#2A201A]">
              {job.pay && <div className="p-6"><span className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-teal"><Coins className="h-4 w-4" /> Pay</span><p className="mt-2 font-display text-2xl">{job.pay}</p></div>}
              {job.req && <div className="p-6"><span className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-teal"><ScrollText className="h-4 w-4" /> Requirements</span><p className="mt-2 text-[#b8b0a4]">{job.req}</p></div>}
              {job.tag && <div className="p-6"><span className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-teal"><Compass className="h-4 w-4" /> Category</span><p className="mt-2 text-[#b8b0a4]">{job.tag}</p></div>}
            </div>
          </Reveal>
        </div>
        {others.length > 0 && (
          <div className="mt-24">
            <Overline>Other trades</Overline>
            <div className="mt-8 grid gap-6 sm:grid-cols-3">
              {others.map((o) => (
                <Link key={o.slug} to={`/jobs/${o.slug}`}
                  className="group border border-[#2A201A] bg-surface/30 p-6 hover:border-teal/40 transition-colors">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-teal">{o.tag}</span>
                  <h3 className="mt-2 font-display text-2xl font-light group-hover:text-teal transition-colors">{o.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>
    </>
  );
}
